import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';
import { effectiveOf, isPrnDose } from '@/common/medicationPass';
import { useAuthStore } from '@/stores/auth';

const RESOURCE = 'MedicationAdministration';

// Carried on the error when an amendment lost a race, so the dialog can say so rather than offer a retry.
export const AMENDMENT_CONFLICT = 'amendment-conflict';

export const STATUS_REASON_SYSTEM = 'http://fhir.bestnotes.com/r4/CodeSystem/medication-administration-status-reasons';

/** Why a dose that WAS given fell outside its window. Never statusReason - that means not given. */
export const LATE_REASON_SYSTEM = 'http://fhir.bestnotes.com/r4/CodeSystem/medication-administration-late-reasons';

/** Why less than the ordered amount was given. One record carries both the amount and the reason. */
export const SHORTFALL_REASON_SYSTEM = 'http://fhir.bestnotes.com/r4/CodeSystem/medication-administration-shortfall-reasons';

/** Which PRN limit a dose was given past. Coded onto the record so a later edit to the order cannot change what the record says was exceeded. */
export const LIMIT_FLAG_SYSTEM = 'http://fhir.bestnotes.com/r4/CodeSystem/medication-administration-limit-flags';

/** How a dose came to be given. Absent on an ordinary scheduled or as-needed dose; 'unscheduled' on one given outside both. */
export const ADMINISTRATION_CATEGORY_SYSTEM = 'http://fhir.bestnotes.com/r4/CodeSystem/medication-administration-categories';

const PERFORM_FUNCTION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/med-admin-perform-function';

/** Written only by the day close, never chosen by a nurse — a machine noting that nobody came must not read as a clinical judgment. */
export const SYSTEM_ONLY_STATUS_REASON = 'not-covered';

/**
 * The one refusal a nurse can act on herself, so the screen says what to do rather than that
 * something went wrong. Carried as a code because the sentence belongs to the screen.
 */
export const NO_ADMINISTERING_LOCATION = 'no-administering-location';

export const NO_EPISODE = 'no-episode';

/**
 * The code out of whatever shape the caller passed: a bare code, a Coding, or a CodeableConcept.
 *
 * @param {string|object} statusReason
 * @returns {string|null}
 */
export const statusReasonCode = (statusReason) => {
    if (!statusReason) return null;
    if (typeof statusReason === 'string') return statusReason.trim() || null;
    return statusReason.code || statusReason.coding?.[0]?.code || null;
};

const statusReasonConcept = (statusReason) => {
    if (!statusReason) return undefined;
    if (statusReason.coding) return statusReason;
    const code = statusReasonCode(statusReason);
    // Not on the list: recorded as text with no code invented, the same rule varianceFor follows.
    if (!code) {
        const text = typeof statusReason === 'string' ? statusReason.trim() : statusReason.text;
        return text ? { text } : undefined;
    }
    const display = typeof statusReason === 'string' ? undefined : statusReason.display;
    return { coding: [{ system: STATUS_REASON_SYSTEM, code, display }], text: display };
};

/**
 * A variance, coded against its own system.
 *
 * Takes the same shapes statusReason does — a bare code, a {code, display}, or a whole
 * CodeableConcept — because a nurse can type a reason the list does not carry and that must
 * record as text rather than be dropped or have a code invented for it.
 */
const varianceFor = (variance, system) => {
    if (!variance?.reason) return undefined;

    const { reason, note } = variance;
    if (reason.coding) return { reason, note: note || undefined };

    const code = statusReasonCode(reason);
    const display = typeof reason === 'string' ? undefined : reason.display;
    // Not on the list: recorded as the text it was typed as, with no code invented for it.
    if (!code) return { reason: { text: typeof reason === 'string' ? reason : display }, note: note || undefined };

    return { reason: { coding: [{ system, code, display }], text: display }, note: note || undefined };
};

/**
 * The limits a PRN dose was given past, coded, beside the nurse's own words.
 *
 * A nurse is warned and never blocked, so the reason is what makes the override answerable
 * afterwards — which is why an override without one is refused rather than written empty.
 */
const limitOverrideFor = (limitOverride) => {
    const limits = limitOverride?.limits || [];
    if (!limits.length) return undefined;

    const reason = String(limitOverride?.reason || '').trim();
    if (!reason) throw new Error('useMedicationAdministration.record: a limit override needs a reason.');

    return { limit: { coding: limits.map((code) => ({ system: LIMIT_FLAG_SYSTEM, code })) }, reason };
};

const referenceTo = (resourceType, id, display) => (id ? { id, resourceType, display } : undefined);

/**
 * The performer entries for a dose, always labelled.
 *
 * An unlabelled performer is forever ambiguous between 'the nurse, unwitnessed' and 'someone,
 * role unknown', and no migration can recover the difference — which is why the label is
 * stamped now even though single-signature is what ships.
 *
 * @param {object|null} performer
 * @param {object|null} witness
 * @returns {Array<object>|undefined}
 */
const performersFor = (performer, witness) => {
    const performers = [];
    if (performer?.id) performers.push({ function: { coding: [{ system: PERFORM_FUNCTION_SYSTEM, code: 'performer' }] }, actor: performer });
    if (witness?.id) performers.push({ function: { coding: [{ system: PERFORM_FUNCTION_SYSTEM, code: 'witness' }] }, actor: witness });
    return performers.length ? performers : undefined;
};

// A read comes back as fhirworks models, whose private fields must not be patched back.
const plain = (value) => (typeof value?.toJSON === 'function' ? value.toJSON() : value);

const idOf = (value) => (typeof value === 'string' ? value : value?.id) || null;

/**
 * Who gave the dose that already answers this slot, when, and where — the three facts the
 * nurse who lost the race has to be shown.
 */
const conflictFrom = (administration) => ({
    performer: administration?.performer?.[0]?.actor ?? null,
    effective: effectiveOf(administration),
    location: administration?.administeringLocation ?? null,
});

/**
 * Writing and amending MedicationAdministration records.
 *
 * The write path goes through $httpFhirApi and never through DataProvider: DataProvider
 * replaces config.headers, which would drop If-None-Exist and silently take away the
 * double-dose protection with no visible symptom.
 *
 * @returns {{record: Function, amend: Function, recordEffectiveness: Function}}
 */
export function useMedicationAdministration() {
    const authStore = useAuthStore();

    const performerReference = () => authStore.aidboxPersonReference;

    const medicationOf = (entry, medicationId) => (entry?.medications || []).find((item) => item?.id === medicationId)?.medication;

    // The winner of a race the conditional create lost — Aidbox answered with a constraint
    // violation rather than a 200, so the existing record has to be fetched to describe it.
    const findExisting = async (medicationId, slot) => {
        try {
            const response = await $httpFhirApi.get(`${RESOURCE}?request=${encodeURIComponent(medicationId)}&scheduledDateTime=${encodeURIComponent(slot)}&status:not=entered-in-error&_count=1`, {
                errorHandle: false,
            });
            const records = Array.isArray(response?.data) ? response.data : [];
            return records.find((item) => item?.resourceType === RESOURCE && item?.scheduledDateTime === slot) ?? null;
        } catch {
            return null;
        }
    };

    /**
     * Record one dose.
     *
     * @param {object} options
     * @param {object} options.entry - the roster entry { patient, episodeId, medications[] };
     *   required, because an Expectation carries only medicationId and subject, context and
     *   medication[x] cannot be derived from it
     * @param {object} [options.expected] - the Expectation this record answers. Absent means a
     *   stand-alone dose: no slot, no conditional create, and any number may exist in a day
     * @param {string} [options.status] - completed | not-done
     * @param {object} [options.doseGiven] - Quantity; defaults to the ordered dose
     * @param {string|object} [options.statusReason]
     * @param {{reason: string|object, note?: string}} [options.timingVariance] - why a dose given
     *   outside its window was still given then
     * @param {{reason: string|object, note?: string}} [options.doseVariance] - why less than the
     *   ordered amount was given
     * @param {string} [options.note]
     * @param {string} [options.effectiveAt] - when the dose was actually given, as an instant.
     *   Defaults to now. A dose given off a paper schedule and typed up later carries the time it
     *   was given, not the time it was typed - which is also what decides whether it was late
     * @param {object} [options.identityVerification] - { method, verifiedAt, verifiedBy }
     * @param {string} [options.indication] - what a PRN dose was given for, in the nurse's words
     * @param {{limits: string[], reason: string}} [options.limitOverride] - the PRN limits this dose
     *   passed and why it was given anyway. Refused without a reason
     * @param {object} [options.route] - a CodeableConcept for a dose that answers no expectation;
     *   an expectation's own route wins where there is one
     * @param {string} [options.category] - a code from medication-administration-categories, for a
     *   dose given outside both the schedule and any as-needed order
     * @param {string} [options.locationId]
     * @param {string} [options.medicationId] - the order a stand-alone dose fulfils
     * @param {object} [options.witness] - a practitioner reference from the witness verification
     *   endpoint, written as performer[1]. Nothing calls this in the shipping single-signature
     *   flow; the path exists so switching witnessing on is a UI change only
     * @returns {Promise<{administration: object, conflict: null|{performer: object|null, effective: string|null, location: object|null}}>}
     */
    const record = async ({
        entry,
        expected = null,
        status = 'completed',
        doseGiven,
        statusReason,
        timingVariance,
        doseVariance,
        note,
        effectiveAt,
        identityVerification,
        indication,
        limitOverride,
        route,
        category,
        locationId,
        medicationId,
        witness,
    } = {}) => {
        if (!entry) throw new Error('useMedicationAdministration.record: entry is required.');

        const reasonCode = statusReasonCode(statusReason);
        // A typed reason satisfies this too: what a not-done record must never be is silent.
        if (status === 'not-done' && !statusReasonConcept(statusReason)) throw new Error('useMedicationAdministration.record: a not-done record needs a statusReason.');
        if (reasonCode === SYSTEM_ONLY_STATUS_REASON) throw new Error(`useMedicationAdministration.record: '${SYSTEM_ONLY_STATUS_REASON}' is written by the day close and cannot be supplied.`);

        // The admission is what the record files against, so a write without one would produce an
        // administration no printed patient record could ever place. Loud, because the alternative
        // is a dose in the chart that belongs to no stay.
        if (!entry?.episodeId) {
            const noEpisode = new Error('useMedicationAdministration.record: this dose has no episode of care to record against.');
            noEpisode.code = NO_EPISODE;
            throw noEpisode;
        }

        // Where it was given, which is not where it was meant to be given. Loud for the same
        // reason as the episode: a record naming nowhere is invisible to every location-scoped
        // read afterwards, and it cannot be repaired later - nothing else knows where she stood.
        if (!locationId) {
            const noLocation = new Error('useMedicationAdministration.record: this dose has no location to record against.');
            noLocation.code = NO_ADMINISTERING_LOCATION;
            throw noLocation;
        }

        const orderId = expected?.medicationId ?? medicationId ?? null;
        const now = new Date().toISOString();
        const performer = performerReference();
        const dose = doseGiven ?? expected?.dose;
        const reason = statusReasonConcept(statusReason);
        const override = limitOverrideFor(limitOverride);

        const payload = {
            resourceType: RESOURCE,
            status,
            subject: referenceTo('Patient', idOf(entry.patient)),
            context: referenceTo('EpisodeOfCare', entry.episodeId),
            medication: medicationOf(entry, orderId),
            // What the nurse says about the dose. `now` only where she offered no time of her own.
            effective: { dateTime: effectiveAt || now },
            // What the system says about the entry, always. Where the two differ the dose was
            // documented after the fact - off a paper form, or a time adjusted at the window - and
            // whether documentation was contemporaneous is not answerable from `effective` alone.
            recordedAt: now,
            performer: performersFor(performer, witness),
            request: referenceTo('MedicationRequest', orderId),
            statusReason: reason ? [reason] : undefined,
            reasonCode: indication ? [{ text: indication }] : undefined,
            category: category ? { coding: [{ system: ADMINISTRATION_CATEGORY_SYSTEM, code: category }] } : undefined,
            note: note ? [{ author: { Reference: performer }, time: now, text: note }] : undefined,
            dosage: dose && status !== 'not-done' ? { dose, route: expected?.route ?? route } : undefined,
            administeringLocation: referenceTo('Location', locationId),
            timingVariance: varianceFor(timingVariance, LATE_REASON_SYSTEM),
            doseVariance: varianceFor(doseVariance, SHORTFALL_REASON_SYSTEM),
            limitOverride: override,
            identityVerification: identityVerification
                ? { ...identityVerification, verifiedAt: identityVerification.verifiedAt || now, verifiedBy: identityVerification.verifiedBy || performer }
                : undefined,
        };

        // Omitted for a stand-alone dose: without a slot the record falls outside the partial
        // unique index, which is what lets a patient in crisis have three doses in an hour.
        if (expected) {
            payload.scheduledDateTime = expected.slot;
            if (expected.sequence !== null && expected.sequence !== undefined) payload.dosageInstructionSequence = expected.sequence;
        }

        const config = { errorHandle: false };
        // entered-in-error excluded to match the partial unique index and findExisting: a voided
        // record would otherwise keep matching its own slot and block the correction.
        if (expected && orderId) config.headers = { 'If-None-Exist': `request=${orderId}&scheduledDateTime=${expected.slot}&status:not=entered-in-error` };

        try {
            const response = await $httpFhirApi.post(RESOURCE, payload, config);
            // 200 means If-None-Exist matched: another nurse already answered this slot. Never overwrite.
            if (response?.status === 200) return { administration: response.data, conflict: conflictFrom(response.data) };
            return { administration: response?.data, conflict: null };
        } catch (err) {
            if (!expected || !orderId) throw err;
            // Two writers can still pass the conditional create's lookup at once; Postgres rejects
            // the loser. That is the same lost race and has to read as a conflict, not an error.
            const existing = await findExisting(orderId, expected.slot);
            if (!existing) throw err;
            $log.warning('useMedicationAdministration: slot already recorded', `${orderId} @ ${expected.slot}`, err?.message);
            return { administration: existing, conflict: conflictFrom(existing) };
        }
    };

    /**
     * Amend a record in place. Produces no second record — every expected dose has exactly one answer.
     *
     * Nothing here gates on how old the record is: amending a machine-written 'not covered' into
     * what really happened is the ordinary morning path, and a record that locks with age simply
     * stays wrong. What is required instead is a reason, at every age, and an audit trail — the
     * prior record verbatim on a Provenance, so an amendment can never quietly replace what the
     * bedside actually said.
     *
     * `entered-in-error` is refused. It means the record should never have existed, which is a
     * different act from amending one that should.
     *
     * @param {object} administration
     * @param {object} options
     * @param {string} [options.status] - completed | not-done
     * @param {string|object} [options.statusReason] - required alongside a 'not-done' status
     * @param {string} [options.effectiveAt] - ISO instant
     * @param {object} [options.doseGiven] - Quantity
     * @param {string} [options.note]
     * @param {string} options.amendmentReason - why the record is being amended. Required
     * @returns {Promise<object>}
     */
    const amend = async (administration, { status, statusReason, effectiveAt, doseGiven, note, amendmentReason } = {}) => {
        const id = administration?.id;
        if (!id) throw new Error('useMedicationAdministration.amend: a persisted administration is required.');

        const why = String(amendmentReason || '').trim();
        if (!why) throw new Error('useMedicationAdministration.amend: an amendment needs a reason.');

        if (status === 'entered-in-error') throw new Error("useMedicationAdministration.amend: 'entered-in-error' is not an amendment.");

        const reasonCode = statusReasonCode(statusReason);
        if (status === 'not-done' && !statusReasonConcept(statusReason)) throw new Error('useMedicationAdministration.amend: a not-done amendment needs a statusReason.');

        // Read before anything is written: the version named on the trail has to be the one this
        // patch is about to replace. The caller's copy cannot answer that - the screens reach here
        // with records trimmed by `_elements`, and meta is not among the fields they ask for.
        const stored = await $httpFhirApi.get(`${RESOURCE}/${id}`, { errorHandle: false }).catch((err) => {
            $log.error('useMedicationAdministration.amend: could not read the record being amended', `${RESOURCE}/${id}: ${err?.message}`, err?.stack);
            return null;
        });
        // The version being replaced, named rather than copied. A contained snapshot would put a
        // full MedicationAdministration inside a Provenance, and Provenance is not gated on
        // view_med_list - the copy would be readable by anyone the medication policy excludes.
        // _history is covered by medication-administration-read, so the prior values stay behind
        // the gate that owns them.
        const priorVersion = stored?.data?.meta?.versionId ?? null;

        const patch = [];
        if (status) patch.push({ op: 'add', path: '/status', value: status });
        if (effectiveAt) patch.push({ op: 'add', path: '/effective', value: { dateTime: effectiveAt } });
        if (doseGiven && status !== 'not-done') {
            // '/dosage/dose' would fail on a not-done record, which carries no dosage at all.
            const dosage = plain(administration.dosage);
            patch.push({ op: 'add', path: '/dosage', value: { ...(dosage || {}), dose: doseGiven } });
        }
        // A record being administered keeps no reason for not being given — 'not covered' above all.
        if (status === 'completed' && administration.statusReason?.length) patch.push({ op: 'remove', path: '/statusReason' });
        else if (status !== 'completed' && statusReasonConcept(statusReason)) patch.push({ op: 'add', path: '/statusReason', value: [statusReasonConcept(statusReason)] });

        // The mirror of record()'s rule that a not-done record carries no dosage. Without it a dose
        // amended from given to refused keeps the amount, and every screen reads the two off each
        // other: 'Refused · 2 tablet given' on a legal record.
        if (status === 'not-done' && (stored?.data?.dosage || administration.dosage)) patch.push({ op: 'remove', path: '/dosage' });

        const performer = performerReference();
        const now = new Date().toISOString();
        // Appended to the stored notes: the screen's copy is trimmed and may predate another amendment.
        if (note) patch.push({ op: 'add', path: '/note', value: [...(stored?.data?.note || administration.note || []), { author: { Reference: performer }, time: now, text: note }] });

        // A reason with nothing to apply it to is not an amendment. Writing the Provenance anyway
        // marks the record Amended on every surface for a change nobody made.
        if (!patch.length) throw new Error('useMedicationAdministration.amend: an amendment has to change something.');

        // Pinned to the version read above, so an amendment racing another cannot overwrite it
        // with values - and a trail entity - computed from the record before it.
        const config = { errorHandle: false, ...(priorVersion ? { headers: { 'If-Match': `W/"${priorVersion}"` } } : {}) };
        let response;
        try {
            response = await $httpFhirApi.patch(`${RESOURCE}/${id}?_method=json-patch`, patch, config);
        } catch (err) {
            if (err?.response?.status !== 412) throw err;
            const changed = new Error('This dose was changed by someone else while you were amending it. Reload it and amend it again.');
            changed.code = AMENDMENT_CONFLICT;
            changed.cause = err;
            throw changed;
        }
        const amended = response?.data ?? administration;

        // After the record, never before: a Provenance written first would claim an amendment that
        // then failed. The performer is untouched throughout — who gave the dose is not what
        // changed, and the amender is this agent.
        //
        // The record has already changed by this point, so a failure below leaves it amended and
        // unmarked. The caller has to be able to say that precisely: telling a nurse the amendment
        // failed invites her to make it a second time, and there is no undo.
        try {
            await $httpFhirApi.post(
                'Provenance',
                {
                    resourceType: 'Provenance',
                    target: [{ id, resourceType: RESOURCE }],
                    recorded: now,
                    agent: [{ who: performer }],
                    reason: [{ text: why }],
                    // Omitted rather than guessed at when the read above failed: a trail that names
                    // the wrong version is worse than one that names none, and who/when/why survive.
                    entity: priorVersion ? [{ role: 'revision', what: { reference: `${RESOURCE}/${id}/_history/${priorVersion}` } }] : undefined,
                },
                { errorHandle: false },
            );
        } catch (err) {
            err.amendmentApplied = true;
            throw err;
        }

        return amended;
    };

    /**
     * Say whether an as-needed dose worked.
     *
     * Appended to whatever entries the stored record already holds, never written over them, and
     * by whoever is following up - who gave the dose is not what this changes. Not an amendment:
     * no Provenance is written, so the dose is never marked Amended for it.
     *
     * Pinned to the version read, so two nurses following up at once cannot drop one another's
     * entry. A lost race re-reads and appends once more: entries only ever accumulate, so the retry
     * can lose nothing.
     *
     * @param {object} administration
     * @param {string} text
     * @returns {Promise<object>} the updated administration
     */
    const recordEffectiveness = async (administration, text) => {
        const id = administration?.id;
        if (!id) throw new Error('useMedicationAdministration.recordEffectiveness: a persisted administration is required.');

        const written = String(text || '').trim();
        if (!written) throw new Error('useMedicationAdministration.recordEffectiveness: an effectiveness entry needs text.');

        const author = performerReference();

        const append = async () => {
            // Read rather than trusted from the screen: its copy is trimmed and may predate another
            // nurse's entry, and a stale screen may be offering this on a dose that is no longer PRN.
            const stored = (await $httpFhirApi.get(`${RESOURCE}/${id}`, { errorHandle: false }))?.data;
            if (!isPrnDose(stored)) throw new Error('useMedicationAdministration.recordEffectiveness: only a given as-needed dose takes an effectiveness entry.');

            const version = stored?.meta?.versionId;
            const config = { errorHandle: false, ...(version ? { headers: { 'If-Match': `W/"${version}"` } } : {}) };
            const entries = [...(stored.effectiveness || []).map(plain), { text: written, author, time: new Date().toISOString() }];
            const response = await $httpFhirApi.patch(`${RESOURCE}/${id}?_method=json-patch`, [{ op: 'add', path: '/effectiveness', value: entries }], config);
            return response?.data;
        };

        try {
            return await append();
        } catch (err) {
            if (err?.response?.status !== 412) throw err;
            return append();
        }
    };

    return { record, amend, recordEffectiveness };
}

export default useMedicationAdministration;
